// ØVINGSKILDEN — den delte, ærlige måten å fylle en tom sensorflate med data.
//
// ══ HVORFOR DENNE FILA FINNES ═══════════════════════════════════════════════
// En sensorflate bygges alltid FØR broen som skal mate den. Uten bro er flata
// helt tom, og en tom flate kan ikke vurderes, demonstreres eller øves på — man
// ser ikke om «stille i 40 s» faktisk SER stille ut på en liste uten rader. Både
// NJORD og ULL hadde derfor hver sin lokale demokilde inne i `main.ts`, ordrett
// like på ~25 linjer. Den er løftet hit, fordi de to flatene skal ha ETT svar på
// hva øvingsdata er og hvordan den merkes — ikke to som kan gli fra hverandre.
//
// ══ HVORFOR «ØVING» OG IKKE «DEMO» ═════════════════════════════════════════
// «Demo» beskriver hvem dataen er for (den som ser på). «Øving» beskriver hva
// den brukes til (å trene på flata). Det er det siste som betyr noe for den som
// står med skjermen, og ordet ØVING er dessuten det ordet en operatør allerede
// har i munnen — det roper han over sambandet uten å tenke seg om. Derfor er
// flagget i datamodellen `ovelse`, ikke `demo`.
//
// ══ DE FIRE UFRAVIKELIGE KRAVENE, OG HVOR DE HÅNDHEVES ═════════════════════
// Øvingsdata som forveksles med ekte data er verre enn ingen data. Kravene er
// derfor ikke retningslinjer, de er kode:
//
//   1. FLAGGET FØLGER DATAEN. `lagOvelseskilde` stempler HVER melding den
//      slipper ut med `ovelse: true` (`merkOvelse`). Det skjer i ÉN funksjon på
//      ÉN utgang, så en manuslinje som glemmer flagget finnes ikke — og appen
//      bærer flagget videre inn i lageret sitt, slik at kortet og raden også
//      viser det. Flagget er i DATAMODELLEN, ikke i UI-et.
//   2. MERKINGEN ER PERMANENT. `ovelsebanner` (topp, full bredde) +
//      `settOvelsesramme` (ramme rundt hele flata) i `tilstand.ts`. Samme
//      visuelle språk som COP-ens «DEMO — IKKE EKTE DATA»-stripe: `--danger`,
//      versaler, alltid synlig. Et utsnitt av skjermen skal ikke kunne
//      forveksles med et ekte situasjonsbilde (grunnloven §1).
//   3. AV SOM STANDARD, OG ALDRI PÅ MED EN EKTE BRO KONFIGURERT. `ovelseTillatt`
//      er porten, og `lagOvelseskilde` KASTER om den kalles med en broadresse.
//      Håndhevet i koden, ikke i UI-et: en knapp som er skjult kan kalles fra
//      konsollen, en konstruktør som kaster kan det ikke.
//   4. INGENTING FRA ØVINGEN GÅR UT. `utgaaendeNektet` er vakta appenes utgående
//      kø SKAL spørre. Den nekter både fordi øvingen er på (alt på flata er da
//      oppdiktet) og fordi kommandoen selv bærer øvingsflagget (dyp skanning) —
//      to uavhengige grunner, fordi den ene alene ville vært nok til å bomme.
//
// ══ ØVINGSDATA SKAL VÆRE REALISTISK UFULLSTENDIG ═══════════════════════════
// Manuset er appens (den vet hva en hydrofon måler), men regelen er felles og
// står her fordi den er lett å bryte: en øving på PERFEKT data lærer operatøren
// feil. Sensorer som alltid ser, posisjoner som alltid finnes og aldre som
// alltid er ferske gir en operatør som ikke gjenkjenner det ekte bildet. Manuset
// SKAL derfor inneholde hull — en sensor som måler og likevel ikke ser noe, en
// kontakt uten posisjon, en node som har vært stille i timer — og pulsen skal
// ikke fylle dem ut.
//
// PLATTFORMFRI: denne fila rører ALDRI DOM (banneret bor i `tilstand.ts`), slik
// at porten og vakta kan testes og kjøres uten nettleser. Se `index.ts`.
/** Sett flagget. Returnerer en NY verdi — inndataen mutteres aldri. */
export function merkOvelse(x) {
    return { ...x, ovelse: true };
}
/**
 * Bærer denne verdien — eller noe inni den — øvingsflagget?
 *
 * Dyp skanning, og det er poenget: en utgående kommando kan referere til
 * øvingsdata gjennom et nøstet felt (en rapport med felter, en liste med
 * observasjoner). En sjekk på bare toppnivået ville sluppet den gjennom.
 *
 * `dybde` finnes for å hindre at en syklisk struktur henger skanningen. Grensa
 * er romslig nok for alt appene faktisk sender, og et objekt som er dypere enn
 * dette har ingenting å gjøre i en utgående kommando.
 */
export function baererOvelseflagg(verdi, dybde = 8) {
    if (dybde < 0 || verdi === null || typeof verdi !== 'object')
        return false;
    if (verdi.ovelse === true)
        return true;
    if (Array.isArray(verdi))
        return verdi.some((v) => baererOvelseflagg(v, dybde - 1));
    for (const v of Object.values(verdi)) {
        if (baererOvelseflagg(v, dybde - 1))
            return true;
    }
    return false;
}
/**
 * Er øvingsdata tillatt på denne flata?
 *
 * ÉN REGEL: er det konfigurert en ekte broadresse, er svaret NEI — alltid, og
 * uten unntak for «broen er nede likevel». Grunnen er operativ: en post med en
 * konfigurert bro er en post i drift, og på en post i drift skal det som står på
 * skjermen komme fra sensorer. Vil man øve, gjør man det på en flate uten bro.
 *
 * Dette er den strengeste av de fire reglene, og den er også den som koster
 * noe: en operatør med en nede bro kan ikke trene. Det er et bevisst valg. Den
 * dagen kravet endres, skal det endres HER, med en begrunnelse — ikke ved at
 * noen legger inn en snarvei i en app.
 */
export function ovelseTillatt(broAdresse) {
    const adresse = (broAdresse ?? '').trim();
    if (adresse.length === 0)
        return { tillatt: true };
    return {
        tillatt: false,
        grunn: 'Denne flata har en konfigurert sensorbro, og da kan øvingsdata IKKE slås på — '
            + 'heller ikke når broen er nede. Det som står på en post i drift skal komme fra sensorer. '
            + 'Øv på en flate uten bro.',
    };
}
/**
 * Har operatøren BEDT om øving? Skilt fra `ovelseTillatt` med vilje: «bedt om»
 * og «får» er to spørsmål, og en flate som blander dem kan ikke forklare hvorfor
 * knappen ikke gjorde noe.
 *
 * `?ovelse=1` slår på, `?ovelse=0` slår av og VINNER over det lagrede valget —
 * ellers ville en operatør som slo av øvingen i går ikke fått en ren flate i dag.
 * Ingenting her slår på av seg selv, og ingenting slår på fordi broen er nede.
 */
export function ovelseBedt(søk = '', lagret = null) {
    const p = new URLSearchParams(søk).get('ovelse');
    if (p === '1')
        return true;
    if (p === '0')
        return false;
    return lagret === '1';
}
/**
 * Øvingsvalget slik det overlever en omlasting.
 *
 * Ligger her og ikke i appene fordi det er en del av øvingskontrakten: valget
 * SKAL være like klebrig i NJORD og ULL, ellers oppfører de to flatene seg ulikt
 * på det ene punktet der forveksling er farligst. `try`/`catch` fordi privat
 * modus og blokkerte cookies kaster på `localStorage` — og et øvingsvalg er ikke
 * viktig nok å velte oppstarten for. Feiler lagringen, er standarden AV, som er
 * den trygge retningen å feile i.
 */
export function lesOvelsevalg(nøkkel) {
    try {
        return globalThis.localStorage?.getItem(nøkkel) ?? null;
    }
    catch {
        return null;
    }
}
export function skrivOvelsevalg(nøkkel, verdi) {
    try {
        globalThis.localStorage?.setItem(nøkkel, verdi);
    }
    catch {
        /* privat modus — se `lesOvelsevalg` */
    }
}
/**
 * Porten og ønsket, slått sammen til det ene svaret appen faktisk trenger.
 * `grunn` er satt når øvingen ble NEKTET av porten, aldri når operatøren bare
 * ikke har bedt om den — «av fordi ingen ba om det» er ikke noe å forklare.
 */
export function ovelseAvgjort(valg) {
    const port = ovelseTillatt(valg.broAdresse);
    if (!port.tillatt) {
        // MERK at vi ikke ser på ønsket her. Er øvingen nektet, er den nektet — og
        // et lagret `ovelse=1` fra en flate uten bro skal ikke kunne følge med når
        // broen senere konfigureres.
        return { på: false, nektet: port.grunn };
    }
    return { på: ovelseBedt(valg.søk ?? '', valg.lagret ?? null), nektet: null };
}
// ── Vakta på den utgående køen ──────────────────────────────────────────────
/**
 * Skal denne utgående kommandoen slippes inn i køen mot SKADE?
 *
 * Returnerer `null` når den kan sendes, og ellers GRUNNEN — som ordrett tekst
 * til operatøren. To uavhengige grunner til å nekte:
 *
 *   * `ovelseAktiv` — er øvingen på, er ALT på flata oppdiktet, også det som
 *     ikke bærer flagget synlig (jegerens eget flagg på en oppdiktet utløsning,
 *     en melding bygget av oppdiktede tall). Da er hele køen stengt.
 *   * `baererOvelseflagg(kommando)` — beltet i tillegg til bukseselene: skulle
 *     en øvingsmerket verdi finne veien inn i en kommando på en flate der
 *     øvingen er slått AV (et utkast skrevet før operatøren slo av), stoppes den
 *     likevel.
 *
 * Vakta NEKTER, den forkaster ikke stille: kallstedet skal si det til operatøren.
 * En handling som forsvinner uten et ord er den verste av alle utfall — da tror
 * han at meldingen er levert.
 */
export function utgaaendeNektet(valg) {
    if (valg.ovelseAktiv) {
        return 'ØVING PÅGÅR: ingenting kan legges i køen eller sendes til SKADE. Alt som står på flata nå '
            + 'er oppdiktet, og en oppdiktet melding i SKADE er en falsk melding. Slå av øvingsdata først.';
    }
    if (baererOvelseflagg(valg.kommando)) {
        return 'Kommandoen bygger på ØVINGSDATA og kan ikke sendes til SKADE. Dette er merket i selve dataen, '
            + 'ikke bare i skjermbildet — bygg meldingen på nytt fra ekte målinger.';
    }
    return null;
}
export const OVELSE_TILKOBLING = {
    tilstand: 'ovelse',
    adresse: '(øvingsdata — ingen bro)',
    forklaring: 'Flata mates av en lokal ØVINGSKILDE. Ingenting av det du ser er målt av en sensor, '
        + 'og ingenting kan sendes til SKADE så lenge øvingen står på.',
};
const OVELSE_PULS_MS = 4000;
/**
 * Lag øvingskilden.
 *
 * KASTER om `broAdresse` er satt. Det er ikke defensiv programmering, det er
 * håndhevingen av krav 3: porten skal ikke kunne omgås ved at noen kaller
 * fabrikken direkte, fra en konsoll eller fra en ny skjerm som ikke kjenner
 * reglene. En kilde som ikke får eksistere, eksisterer ikke.
 */
export function lagOvelseskilde(valg) {
    const port = ovelseTillatt(valg.broAdresse);
    if (!port.tillatt)
        throw new Error(`Øvingskilde nektet: ${port.grunn}`);
    const lyttere = new Set();
    const naa = valg.naa ?? (() => Date.now());
    const pulsMs = valg.pulsMs ?? OVELSE_PULS_MS;
    let startet = false;
    let tikk = 0;
    let timer = null;
    let lukket = false;
    /**
     * DEN ENE UTGANGEN. Hver melding stemples her, og det finnes ingen annen vei
     * ut av kilden. Det er dette som gjør at flagget ikke kan strippes «på veien»
     * — en manusforfatter som glemmer det, blir rettet av denne linja.
     */
    function slippUt(meldinger) {
        let antall = 0;
        for (const m of meldinger) {
            const merket = merkOvelse(m);
            antall++;
            for (const h of lyttere)
                h(merket);
        }
        return antall;
    }
    function puls() {
        if (lukket || !valg.puls)
            return 0;
        tikk++;
        return slippUt(valg.puls(naa(), tikk));
    }
    return {
        tilkobling: () => OVELSE_TILKOBLING,
        påMelding(h) {
            lyttere.add(h);
            if (!startet) {
                startet = true;
                // Neste tikk, så alle abonnementene i appens oppstart er på plass før
                // øyeblikksbildet kommer. Ellers ville den første meldingen gått til en
                // halvt oppkoblet flate.
                globalThis.setTimeout(() => {
                    if (lukket)
                        return;
                    slippUt(valg.start(naa()));
                    if (pulsMs > 0)
                        timer = globalThis.setInterval(puls, pulsMs);
                }, 0);
            }
            return () => lyttere.delete(h);
        },
        påTilkobling(h) {
            // Tilstanden endres ALDRI — det finnes ingen bro å miste. Vi kaller likevel
            // lytteren én gang, så en skjerm som bare tegner på tilkoblingsendring
            // ikke står tom og venter på noe som aldri kommer.
            globalThis.setTimeout(() => { if (!lukket)
                h(OVELSE_TILKOBLING); }, 0);
            return () => undefined;
        },
        send: () => false,
        lukk() {
            lukket = true;
            if (timer !== null)
                globalThis.clearInterval(timer);
            lyttere.clear();
        },
        pulsNå: puls,
    };
}
//# sourceMappingURL=ovelse.js.map